import { memo } from 'preact/compat'
import { useState } from 'preact/hooks'
import type { DashboardHottestLeadItemDto } from '@/types/dashboard'
import { SignalBars } from '@/components/common/SignalBars'
import { rowActivation } from '@/utils/rowActivation'
import { PersonAvatar } from '@/components/common/Avatar'
import { ContactPeekFlyout } from '@/components/contacts/ContactPeekFlyout'

interface HottestLeadsTileProps {
    leads: DashboardHottestLeadItemDto[] | null | undefined
    clientCompanyId: string
    workspaceId: string
    /** Present on the campaign tab, absent on the workspace dashboard. Decides which profile opens. */
    campaignId?: string
    limit?: number
    className?: string
    loading?: boolean
    error?: string | null
    /** The server could not compute this section — shown as broken, not as empty. */
    failed?: boolean
    /** Suppresses the flyout while the dashboard is showing mock data. */
    mock?: boolean
}

/**
 * Rows open the contact flyout at the scope the tile is showing: the workspace dashboard sends the
 * representative lead the server already put on the row, the campaign tab pairs the row's
 * campaign-scoped id with the campaign it is inside.
 */
export const HottestLeadsTile = memo(({ leads, clientCompanyId, workspaceId, campaignId, limit = 6, className = '', loading = false, error = null, failed = false, mock = false }: HottestLeadsTileProps) => {
    const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null)

    return (
        <div className={`tile flex flex-col ${className}`.trim()}>
            <div className="flex items-start justify-between mb-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 leading-tight">
                    Hottest Leads
                </h3>
            </div>
            {loading && (
                <ul className="space-y-4">
                    {Array.from({ length: limit }).map((_, i) => (
                        <li key={i} className="flex items-center gap-3 row-hover p-2 rounded-md">
                            <div className="h-10 w-10 rounded-full bg-shimmer-light animate-shimmer" />
                            <div className="flex-1 min-w-0 flex flex-col gap-1">
                                <div className="h-3 w-32 rounded bg-shimmer-light animate-shimmer" />
                                <div className="h-3 w-24 rounded bg-shimmer-light animate-shimmer" />
                                <div className="h-3 w-20 rounded bg-shimmer-light animate-shimmer" />
                            </div>
                            <div className="h-10 w-16 rounded bg-shimmer-light animate-shimmer" />
                        </li>
                    ))}
                </ul>
            )}
            {!loading && error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}
            {!loading && !error && failed && (
                <div className="text-sm text-red-600 dark:text-red-400">Couldn't load hottest leads</div>
            )}
            {!loading && !error && !failed && leads && leads.length === 0 && (
                <div className="text-sm text-slate-500 dark:text-slate-400">No leads yet</div>
            )}
            {!loading && !error && !failed && leads && leads.length > 0 && (
                <ul className="space-y-3">
                    {leads.map((lead: DashboardHottestLeadItemDto) => {
                        const name = lead.Name || 'Unnamed Lead'
                        const company = lead.CompanyName || '—'
                        const job = lead.JobTitle || '—'
                        const img = lead.ProfileImageUrl
                        // Without a client company the flyout loads but its "Open full profile"
                        // would build a route with an empty segment. Same guard the web tile carries.
                        const canOpen = !mock && !!clientCompanyId && !!workspaceId
                        const activation = rowActivation(canOpen ? () => setSelectedLeadId(lead.LeadId) : null)
                        return (
                            <li key={lead.LeadId}>
                                <div
                                    {...activation}
                                    className={`flex items-center gap-3 p-2 rounded-md ${activation ? 'row-hover cursor-pointer' : ''}`}
                                >
                                    <PersonAvatar
                                        firstName={lead.FirstName}
                                        lastName={lead.LastName}
                                        fullName={name}
                                        profilePicUrl={img}
                                        size="lg"
                                    />
                                    <div className="flex-1 min-w-0 flex flex-col">
                                        <span className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate" title={name}>{name}</span>
                                        <span className="text-2xs text-slate-500 dark:text-slate-400 truncate" title={company}>{company}</span>
                                        <span className="text-2xs text-slate-500 dark:text-slate-400 truncate" title={job}>{job}</span>
                                    </div>
                                    <SignalBars score={lead.SignalScore} class="flex items-end h-10 w-16 gap-1" ariaLabelPrefix="Signal" />
                                </div>
                            </li>
                        )
                    })}
                </ul>
            )}
            {selectedLeadId && (
                <ContactPeekFlyout
                    anchorType="lead"
                    anchorId={selectedLeadId}
                    clientCompanyId={clientCompanyId}
                    workspaceId={workspaceId}
                    campaignId={campaignId}
                    onClose={() => setSelectedLeadId(null)}
                />
            )}
        </div>
    )
})
